import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import api from '@/api';
import { isErrorCode } from '@/api/errors';
import { AlertBox } from '@/common/AlertBox';
import { NotFoundScreen } from '@/common/ErrorScreen';
import { LoadingSpinner } from '@/common/LoadingSpinner';
import { NoteInput } from '@/common/NoteInput';
import { NoteItem } from '@/common/NoteItem';
import { LoadMoreWrapper, TimelineWrapper } from '@/common/timelineStyles';
import { WBSButton } from '@/common/WBSButton';
import { useCurrentUser } from '~/hooks/useCurrentUser';

export const Route = createFileRoute(
  '/_app/_users/members/$memberId/student-notes',
)({
  component: MemberStudentNotesRoute,
});

export default function MemberStudentNotesRoute() {
  const { t } = useTranslation();
  const { memberId } = Route.useParams();
  const studentId = Number(memberId);
  const { me } = useCurrentUser();

  // ---- every hook, unconditionally, before any branch ----
  // An early `return` above the list/mutation hooks makes the first render call
  // fewer hooks than the second, and React throws the instant the probe
  // resolves — i.e. on every successful load, not on an edge case.

  // The tab's own audience, which is NOT the sidebar's `getStudentViewerAccess`
  // set — the difference is stated once, on `STUDENT_NOTE_VIEWERS`
  // (`modules/student_notes/permissions.py`). The probe answers 200 for every
  // authenticated caller, so one query decides the page without reading a
  // status code.
  const { data: access, isError: accessFailed } =
    api.getStudentNoteAccess.useQuery({
      path: { student_id: studentId },
    });

  // No explicit `limit`: the shared default of 10 is already legacy's page size
  // (`buddypress/members/single/student-notes.php:43` `$item_per_pge = 10`).
  // Load More over a typed `offset`/`limit` REPLACES legacy's pager rather than
  // reproducing it (D10). Measured live as admin 1808 on
  // `/members/1091/student-notes/` (3 notes): `?page=0` renders 0 notes, because
  // `:45` computes `(10*0)-10 = -10`; `?page=abc` is a Fatal error page; and the
  // pager `<div>` is always empty because `:50` hands `count/10` — a float — to
  // `paginate_links` as `total`. A typed int answering 422 loses nothing.
  const {
    data,
    isError: notesFailed,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = api.listStudentNotes.useInfiniteQuery(
    { path: { student_id: studentId } },
    {
      initialPageParam: { query: { offset: 0 } },
      // `PaginatedResponse` carries no `hasMore`, so it is derived from the
      // count it does carry, and the offset is the number of rows already held.
      getNextPageParam: (lastPage, allPages) => {
        const loaded = allPages.reduce((n, page) => n + page.data.length, 0);
        return loaded < lastPage.count
          ? { query: { offset: loaded } }
          : undefined;
      },
    },
  );

  const {
    mutateAsync: createNote,
    isPending: creating,
    error: createFailed,
  } = api.createStudentNote.useMutation();
  const { mutateAsync: deleteNote, error: deleteFailed } =
    api.deleteStudentNote.useMutation();

  const [note, setNote] = useState('');
  const [added, setAdded] = useState(false);

  const handleAddNote = async (): Promise<void> => {
    setAdded(false);
    try {
      await createNote({
        path: { student_id: studentId },
        body: { content: note },
      });
      // Cleared BEFORE the refetch is awaited, not after: `creating` goes false
      // the moment the POST resolves, so the refetch window would otherwise be
      // a live Add Note button over content that has already been written.
      // Emptying the box hands the guard back to `disabled={!value.trim()}`.
      setNote('');
      setAdded(true);
    } catch {
      // Rendered from `failure` below.
    }
    // On both paths, so the list is the server's answer rather than ours: a
    // POST that reports failure may still have written the row (the request can
    // succeed and the response be lost), so the failure path is exactly the one
    // that must not trust our local list.
    await api.listStudentNotes.invalidateQueries();
  };

  const handleDeleteNote = async (noteId: number): Promise<void> => {
    setAdded(false);
    try {
      await deleteNote({
        path: { student_id: studentId, note_id: noteId },
      });
    } catch {
      // Rendered from `failure` below. `NoteItem` has already faded the row out
      // by the time this runs and does not fade it back in — legacy's own
      // handler fades on failure too (`assets/js/main-script.js:1272-1295` calls
      // `$li.fadeOut()` in the AJAX callback whatever the response says).
    }
    // On both paths, so the list is the server's answer rather than ours.
    await api.listStudentNotes.invalidateQueries();
  };

  // ---- only now, branch ----
  const items = data?.pages.flatMap((page) => page.data) ?? [];

  // The screen is gated POSITIVELY: it renders only when neither `refused` nor
  // `pending`, i.e. only on an answered `can_view === true`. `access` is also
  // undefined on a 401, a 500 and a dropped connection, and every one of those
  // is a refusal rather than an admission.
  //
  // Each error arm is paired with "and we hold nothing": query-core's reducer
  // sets `status: "error"` on any error action whatever data the query already
  // carries (`@tanstack/query-core/build/modern/query.js:355-366`), so a failed
  // Load More or a failed post-write refetch is an `isError` on a query whose
  // pages are still on screen — and the unguarded form would answer Page not
  // found to a caller the gate has already admitted.
  //
  // `notesFailed` is an arm of `refused` because a 404 from `listStudentNotes`
  // IS the refusal (the list takes the gate, the probe does not); without it a
  // failed list renders the empty state below, telling the viewer this student
  // has no notes when what happened is that the request failed.
  //
  // `data === undefined` is an arm of `pending` because otherwise the spinner
  // clears the instant the PROBE answers while the list is still in flight, and
  // `items` is `[]` on that render — so every load for a student who HAS notes
  // paints the empty state and then swaps it for the list.
  const refused =
    access?.can_view === false ||
    (accessFailed && access === undefined) ||
    (notesFailed && data === undefined);
  const pending = !refused && (access === undefined || data === undefined);
  const failure = createFailed ?? deleteFailed;

  return (
    <MainContent>
      {refused ? (
        // Legacy denies this tab by never registering it in the nav, so the
        // refused viewer gets an HTTP 404 page rather than a permission message
        // (`themes/wbs/modules/global-classes/WBS_Profile_Extension.php:19-23`).
        <NotFoundScreen />
      ) : pending ? (
        <LoadingSpinner centered />
      ) : (
        <>
          {added && (
            <AlertBox variant="success">
              {t('actions.student-note-added-successfully')}
            </AlertBox>
          )}
          {failure && (
            // The 422 arm is unreachable from this UI — `NoteInput` disables Add
            // Note on `!value.trim()` — but legacy renders this exact string for
            // that condition on its redirect back to the tab
            // (`modules/note-system/class-student-notes.php:117-121`), so it is
            // wired rather than dropped. Discriminated on the body: qraft throws
            // the parsed error body and discards the `Response`, so there is no
            // status to read.
            <AlertBox variant="error">
              {isErrorCode(failure, 'validation_error')
                ? t('lessons.please-enter-student-note')
                : t('wbs.something-went-wrong-try')}
            </AlertBox>
          )}
          {/* The composer is rendered to every admitted viewer with no role or
              author test, which is legacy's own rule: the form sits above the
              loop unconditionally (`student-notes.php:5-38`), and write powers
              equal read powers on every arm (R4). */}
          <NoteInput
            value={note}
            onChange={setNote}
            onSubmit={() => {
              void handleAddNote();
            }}
            userName={me?.display_name ?? ''}
            placeholder={t('actions.add-a-note-about')}
            isSubmitting={creating}
          />
          {items.length === 0 ? (
            // Legacy renders an empty `<ul>` and no copy (`student-notes.php:54-56`
            // has no `else`), which reads as a broken screen — and it is the
            // common case: 14 of 717 legacy-range students have any note. D8.
            <AlertBox variant="info">{t('wbs.no-results-found')}</AlertBox>
          ) : (
            <TimelineWrapper>
              {items.map((item) => (
                <NoteItem
                  key={item.id}
                  // Resolved per request from the author's LIVE display name,
                  // never the stored title: `student-notes.php:67` re-derives the
                  // sentence, and 7 of 21 stored titles have already drifted (R7).
                  name={item.author_name ?? t('actions.deleted-user')}
                  content={item.content}
                  createdAt={item.created_at}
                  authorId={item.author_id}
                  // The AUTHOR's avatar, not the viewer's — the mock passed
                  // `me?.avatar` for every row; legacy takes `item_id => $author_id`
                  // (`student-notes.php:63`). R8.
                  avatarUrl={item.avatar_url}
                  onDelete={() => {
                    void handleDeleteNote(item.id);
                  }}
                />
              ))}
              {hasNextPage && (
                <LoadMoreWrapper>
                  <WBSButton
                    wide
                    alternate
                    isLoading={isFetchingNextPage}
                    onClick={() => {
                      void fetchNextPage();
                    }}
                  >
                    {t('wbs.load-more')}
                  </WBSButton>
                </LoadMoreWrapper>
              )}
            </TimelineWrapper>
          )}
        </>
      )}
    </MainContent>
  );
}

const MainContent = styled.main`
  background: var(--color-background);
  flex: 1;
  margin-top: 19px;
  min-width: 0;
`;
